import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import type { UpdateBillInput } from '@accounting/shared';
import { useBill, useUpdateBill } from '../../hooks/useBills';
import { useActiveCompanyId } from '../../stores/useCompanyStore';
import { BillForm, type BillFormValues } from '../../components/bills/BillForm';
import { ApiError } from '../../lib/api';
import { formatCurrency } from '../../lib/formatters';

export const Route = createFileRoute('/_auth/bills/$id_/edit')({
  component: EditBillPage,
});

function EditBillPage() {
  const { id } = Route.useParams();
  const companyId = useActiveCompanyId();
  const navigate = useNavigate();
  const { data: bill, isLoading, error } = useBill(companyId, id);
  const update = useUpdateBill(companyId);

  if (!companyId) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32 }}>
        <p className="muted">No active company.</p>
      </div>
    );
  }
  if (isLoading) return <p className="muted">Loading…</p>;
  if (error || !bill) return <p className="field-error">Failed to load bill.</p>;

  if (bill.status === 'void') {
    return (
      <div>
        <Link to="/bills/$id" params={{ id }} style={{ fontSize: 13 }}>
          ← Bill {bill.billNumber}
        </Link>
        <div className="card" style={{ padding: 20, marginTop: 8 }}>
          <p style={{ marginTop: 0 }}>This bill has been voided and can no longer be edited.</p>
        </div>
      </div>
    );
  }
  const paid = parseFloat(bill.amountPaid);

  const initial: BillFormValues = {
    vendorId: bill.vendorId,
    vendorReference: bill.vendorReference ?? '',
    billDate: bill.billDate,
    dueDate: bill.dueDate,
    terms: bill.terms ?? '',
    memo: bill.memo ?? '',
    lines: bill.lines.map((l) => ({
      accountId: l.accountId,
      description: l.description ?? '',
      quantity: l.quantity,
      unitPrice: l.unitPrice,
    })),
  };

  const errorMessage =
    update.error instanceof ApiError
      ? (update.error.body as { error?: string } | null)?.error ?? 'Could not update bill'
      : update.error?.message;

  return (
    <div>
      <Link to="/bills/$id" params={{ id }} style={{ fontSize: 13 }}>
        ← Bill {bill.billNumber}
      </Link>
      <h1 style={{ fontSize: 24, margin: '8px 0 16px' }}>Edit bill {bill.billNumber}</h1>

      {paid > 0 && (
        <div className="card" style={{ padding: 12, marginBottom: 12, fontSize: 13 }}>
          This bill has <strong>{formatCurrency(bill.amountPaid)}</strong> in payments. They stay
          attached — the balance due recomputes after you save. The new total can’t be below what’s
          already paid.
        </div>
      )}

      <BillForm
        companyId={companyId}
        initial={initial}
        submitLabel="Save changes"
        submitting={update.isPending}
        errorMessage={errorMessage}
        cancelTo="/bills"
        onSubmit={(input) =>
          update.mutate(
            { id, input: input as UpdateBillInput },
            { onSuccess: () => void navigate({ to: '/bills/$id', params: { id } }) },
          )
        }
      />
    </div>
  );
}
